import { useCallback } from 'react'
import {
    collection,
    doc,
    getDoc,
    getDocs,
    setDoc,
    updateDoc,
    deleteDoc,
    query,
    where,
    Timestamp,
} from 'firebase/firestore'
import { firestore, auth } from '../config/firebase'
import { User } from '../../../shared/types/user.types'
import { COLLECTIONS } from '../../../shared/constants/collections'

const API_URL = import.meta.env.VITE_API_URL as string

const convertTimestamps = (data: Record<string, unknown>): Record<string, unknown> => {
    return Object.fromEntries(
        Object.entries(data).map(([key, value]) => {
            if (value instanceof Timestamp) {
                return [key, value.toDate()]
            }
            return [key, value]
        })
    )
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/

const convertRestTimestamps = (data: Record<string, unknown>): Record<string, unknown> => {
    return Object.fromEntries(
        Object.entries(data).map(([key, value]) => {
            if (typeof value === 'string' && ISO_DATE_RE.test(value)) {
                return [key, new Date(value)]
            }
            return [key, value]
        })
    )
}

export interface UserListResult {
    users: User[]
    total: number
}

export interface UserListFilter {
    role?: string
    search?: string
}

interface IUseUserReturn {
    get: (uid: string) => Promise<User | null>
    set: (uid: string, data: Record<string, unknown>) => Promise<void>
    create: (uid: string, data: Record<string, unknown>) => Promise<void>
    remove: (uid: string) => Promise<void>
    list: (filter: UserListFilter, pagination?: { limit?: number; offset?: number }) => Promise<UserListResult>
    findByToken: (token: string) => Promise<User | null>
}

const useUser = (): IUseUserReturn => {

    /**
     * Načte konkrétního uživatele podle jeho UID.
     * @param uid - Firebase UID uživatele
     */
    const get = useCallback(async (uid: string): Promise<User | null> => {
        const ref = doc(firestore, COLLECTIONS.USER, uid)
        const snapshot = await getDoc(ref)

        if (!snapshot.exists()) return null

        return { uid: snapshot.id, ...convertTimestamps(snapshot.data()) } as User
    }, [])

    /**
     * Aktualizuje data konkrétního uživatele.
     * @param uid - Firebase UID uživatele
     * @param data - Objekt s libovolnými poli k aktualizaci
     */
    const set = useCallback(async (uid: string, data: Record<string, unknown>): Promise<void> => {
        const ref = doc(firestore, COLLECTIONS.USER, uid)
        await updateDoc(ref, data)
    }, [])

    /**
     * Vytvoří dokument uživatele (setDoc — přepíše pokud existuje).
     * @param uid - Firebase UID uživatele
     * @param data - Data k uložení
     */
    const create = useCallback(async (uid: string, data: Record<string, unknown>): Promise<void> => {
        const ref = doc(firestore, COLLECTIONS.USER, uid)
        await setDoc(ref, data)
    }, [])

    /**
     * Smaže dokument uživatele.
     * @param uid - Firebase UID uživatele
     */
    const remove = useCallback(async (uid: string): Promise<void> => {
        const ref = doc(firestore, COLLECTIONS.USER, uid)
        await deleteDoc(ref)
    }, [])

    /**
     * Načte stránkovaný seznam uživatelů z API (obohaceno o Firebase Auth data).
     * @param params - Volitelné parametry stránkování
     */
    const list = useCallback(async (filter: UserListFilter, pagination?: { limit?: number; offset?: number }): Promise<UserListResult> => {
        const token = await auth.currentUser?.getIdToken()
        if (!token) throw new Error('User not authenticated')

        const query = new URLSearchParams()
        if (pagination) {
            query.set('limit', String(pagination.limit ?? 100))
            query.set('offset', String(pagination.offset ?? 0))
        }
        if (filter.role) query.set('role', filter.role)
        if (filter.search) query.set('search', filter.search)
        const qs = query.toString()
        const url = `${API_URL}/api/users${qs ? `?${qs}` : ''}`

        const res = await fetch(url, {
            headers: { Authorization: `Bearer ${token}` },
        })
        if (!res.ok) throw new Error('Failed to fetch users')

        const data = await res.json()
        return { users: (data.users as Record<string, unknown>[]).map(convertRestTimestamps) as unknown as User[], total: data.pagination.total as number }
    }, [])

    /**
     * Najde pozvánku (pending user doc) podle tokenu.
     * @param token - Unikátní token pozvánky
     */
    const findByToken = useCallback(async (token: string): Promise<User | null> => {
        const q = query(collection(firestore, COLLECTIONS.USER), where('token', '==', token))
        const snapshot = await getDocs(q)

        if (snapshot.empty) return null

        const d = snapshot.docs[0]
        return { uid: d.id, ...convertTimestamps(d.data()) } as User
    }, [])

    return { get, set, create, remove, list, findByToken }
}

export default useUser
